import { useSuspenseQuery } from "@tanstack/react-query"
import { trpc } from "app/trpc"
import styled from "@emotion/styled"
import { Dialog, Tabs } from "radix-ui"
import { Value } from "@stringtale/react"
import { ContentSubjectType } from "db/enums"
import { motion } from "framer-motion"
import { useMemo, useState } from "react"
import { DARK_BLUE_COLOR, LIGHT_ORANGE_COLOR, ORANGE_COLOR } from "theme/colors"
import { MODAL_OVERLAY_Z_INDEX, MODAL_Z_INDEX } from "theme/consts"
import { poppins } from "theme/fonts"
import { PX12, PX14, PX24, PX28 } from "theme/sizes"
import { MODAL_BOX_SHADOW } from "theme/styles"
import Group from "ui/Group"
import Hr from "ui/Hr"
import * as Modal from "ui/Modal"
import { RADIUS } from "ui/Modal"
import * as ScrollArea from "ui/ScrollArea"
import Stack from "ui/Stack"
import Text from "ui/Text"
import Title from "ui/Title"
import Image from "web/src/files/components/Image"

const Root = styled(motion.div)`
  width: calc(100vw - 42px);
  height: calc(100vh - 50px);
  max-width: 660px;
  position: fixed;
  color: ${DARK_BLUE_COLOR};

  inset: 0;
  margin: auto;

  z-index: ${MODAL_Z_INDEX};
  background-color: white;
  border-radius: ${RADIUS};
  font-family: ${poppins.style.fontFamily};
  ${MODAL_BOX_SHADOW};
`

const Content = styled.div`
  display: flex;
  flex-direction: column;
  /* grid-template-rows: 45px min-content auto; */
  gap: 20px;
  padding: 40px;
  box-sizing: border-box;
  height: inherit;
  overflow: hidden;
  height: 100%;
`

const Overlay = styled(Dialog.Overlay)`
  background-color: rgba(0, 0, 0, 0.5);
  position: fixed;
  inset: 0;
  z-index: ${MODAL_OVERLAY_Z_INDEX};
`.withComponent(motion.div)

const Header = styled(Stack)``

const TabList = styled(Tabs.List)`
  display: flex;
  gap: 2px;
`

const Tab = styled(Tabs.Trigger)`
  border: none;
  border-radius: 4px 4px 2px 2px;
  border-bottom: 4px solid #fff0e9;
  background: none;
  font-size: ${PX14};
  color: ${DARK_BLUE_COLOR};
  padding: 8px 16px;
  cursor: pointer;
  &[data-state="active"] {
    cursor: default;
    border-bottom: 4px solid ${ORANGE_COLOR};
    background-color: ${LIGHT_ORANGE_COLOR};
  }
`

const getParticipationModalTitle = (contentSubjectType: ContentSubjectType) => {
  switch (contentSubjectType) {
    case ContentSubjectType.MUSIC:
      return (
        <Value
          name="apps.web.src.lessons.components.participationicons.muziek_meedoen_iconen_toelichting"
          version="1"
        >
          Toelichting meedoen-iconen muziek
        </Value>
      )
    case ContentSubjectType.DRAMA:
      return (
        <Value
          name="apps.web.src.lessons.components.participationicons.drama_meedoen_iconen_toelichting"
          version="1"
        >
          Toelichting meedoen-iconen drama
        </Value>
      )
    case ContentSubjectType.DANCE:
      return (
        <Value
          name="apps.web.src.lessons.components.participationicons.dans_meedoen_iconen_toelichting"
          version="1"
        >
          Toelichting meedoen-iconen dans
        </Value>
      )
  }
}

const ParticipationIconModalContent = ({
  defaultTab = ContentSubjectType.MUSIC,
}: {
  defaultTab?: ContentSubjectType
}) => {
  const [tab, setTab] = useState<ContentSubjectType>(defaultTab)
  const { data: participationIcons } = useSuspenseQuery(
    trpc.search.getParticipationIcons.queryOptions({})
  )
  const dramaIcons = useMemo(
    () =>
      participationIcons?.filter(
        (participationIcon) =>
          participationIcon.contentSubject === ContentSubjectType.DRAMA
      ),
    [participationIcons]
  )
  const danceIcons = useMemo(
    () =>
      participationIcons?.filter(
        (participationIcon) =>
          participationIcon.contentSubject === ContentSubjectType.DANCE
      ),
    [participationIcons]
  )
  const musicIcons = useMemo(
    () =>
      participationIcons?.filter(
        (participationIcon) =>
          participationIcon.contentSubject === ContentSubjectType.MUSIC
      ),
    [participationIcons]
  )

  const currentSubjectIcons = useMemo(() => {
    switch (tab) {
      case ContentSubjectType.DRAMA:
        return dramaIcons
      case ContentSubjectType.DANCE:
        return danceIcons
      case ContentSubjectType.MUSIC:
        return musicIcons
      default:
        return participationIcons
    }
  }, [danceIcons, dramaIcons, musicIcons, participationIcons, tab])

  return (
    <>
      <Dialog.Portal forceMount>
        <Dialog.Content forceMount>
          <Root
            key="addLessonModal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{
              duration: 0.3,
            }}
          >
            <Content>
              <ScrollArea.Root type="scroll">
                <ScrollArea.Viewport>
                  <Stack gap="20px">
                    <Header gap="16px" justify="flex-start">
                      <Tabs.Root
                        value={tab}
                        onValueChange={(value: ContentSubjectType) =>
                          setTab(value)
                        }
                      >
                        <Tabs.List asChild>
                          <TabList>
                            <Tab value={ContentSubjectType.MUSIC}>
                              <Text bold={tab === ContentSubjectType.MUSIC}>
                                <Value
                                  name="apps.web.src.lessons.components.participationicons.muziek_iconen"
                                  version="1"
                                >
                                  Muziek-iconen
                                </Value>
                              </Text>
                            </Tab>
                            <Tab value={ContentSubjectType.DANCE}>
                              <Text bold={tab === ContentSubjectType.DANCE}>
                                <Value
                                  name="apps.web.src.lessons.components.participationicons.dans_iconen"
                                  version="1"
                                >
                                  Dans-iconen
                                </Value>
                              </Text>
                            </Tab>
                            <Tab value={ContentSubjectType.DRAMA}>
                              <Text bold={tab === ContentSubjectType.DRAMA}>
                                <Value
                                  name="apps.web.src.lessons.components.participationicons.drama_iconen"
                                  version="1"
                                >
                                  Drama-iconen
                                </Value>
                              </Text>
                            </Tab>
                          </TabList>
                        </Tabs.List>
                      </Tabs.Root>
                    </Header>
                    <Stack gap={PX24}>
                      <Stack gap="8px">
                        <Title size={PX24} weight="900">
                          {getParticipationModalTitle(tab)}
                        </Title>
                        <Text size={PX14}>
                          <Value
                            name="apps.web.src.lessons.components.participationiconmodalcontent.icon_explanation"
                            version="1"
                          >
                            Binnen 123ZING werken we met iconen die staan voor
                            verschillende actieve werkvormen. Zo werk je
                            spelenderwijs aan verschillende domeinen van muziek,
                            dans en drama.
                          </Value>
                        </Text>
                      </Stack>
                      <Hr />
                    </Stack>
                    <Stack gap={PX28} justify="center">
                      {currentSubjectIcons?.map((participationIcon) => (
                        <Group key={participationIcon.id} gap={PX12}>
                          {participationIcon.image && (
                            <Image
                              alt=""
                              file={participationIcon.image}
                              width={40}
                              height={40}
                              // style={{ objectFit: "contain" }}
                            />
                          )}
                          <Stack justify="center">
                            <Title size={PX14}>
                              {participationIcon.displayTitle}
                            </Title>
                            <Text size={PX14}>
                              {participationIcon.explanation}
                            </Text>
                          </Stack>
                        </Group>
                      ))}
                    </Stack>
                  </Stack>
                </ScrollArea.Viewport>
                <ScrollArea.Scrollbar>
                  <ScrollArea.Thumb />
                </ScrollArea.Scrollbar>
              </ScrollArea.Root>
              <Dialog.Close asChild>
                <Modal.Close />
              </Dialog.Close>
            </Content>
          </Root>
        </Dialog.Content>
        <Overlay
          key="addLessonOverlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{
            duration: 0.3,
          }}
        />
      </Dialog.Portal>
    </>
  )
}

export default ParticipationIconModalContent
